import React, { PropsWithChildren } from 'react';
import {
  DndContext,
  DndContextProps,
  DragEndEvent,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDispatch, useSelector } from 'react-redux';

import { selectAssignments } from '../../../system-details/systemDetailsSlice';
import {
  selectAllAppliances,
  selectAllCircuits,
  setAllCircuits,
  updateAppliance,
} from '../circuitApplianceConfigSlice';
import { AVAILABLE_CIRCUITS_DROP_AREA_ID } from './appliance-config-helpers';

export const DragAndDropContext = ({ children }: PropsWithChildren) => {
  const dispatch = useDispatch();
  const allAppliances = useSelector(selectAllAppliances);
  const allCircuits = useSelector(selectAllCircuits);
  const assignments = useSelector(selectAssignments);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 4 } })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const draggedCircuitId = active.id;
    const dropAreaId = over?.id;

    // No action if not dragged over another element.
    if (!over) return;

    const activeCircuit = allCircuits.find((circuit) => circuit.oem_circuit_id === draggedCircuitId);
    if (!activeCircuit) return; // No action if the circuit is not found.

    if (dropAreaId === AVAILABLE_CIRCUITS_DROP_AREA_ID) {
      // No action if the circuit is already available.
      const isAlreadyAvailable = allCircuits.some(
        (circuit) => circuit.oem_circuit_id === draggedCircuitId && !circuit.appliance_id
      );
      if (isAlreadyAvailable) return;

      // If the circuit is dragged over the available circuits, it is removed from the appliance.
      dispatch(
        setAllCircuits([
          ...allCircuits.filter((circuit) => circuit.oem_circuit_id !== draggedCircuitId),
          { ...activeCircuit, appliance_id: null },
        ])
      );

      // No action if the circuit is already assigned to an appliance.
    } else {
      const applianceAreaToDrop = allAppliances.find((appliance) => appliance.appliance_id === dropAreaId);
      if (!applianceAreaToDrop) return;

      const isEmptyApplianceArea = !applianceAreaToDrop.circuits.length;

      // If the circuit is dragged over an invalid drop area, no action is taken.
      // allow dropping in appliance area without circuits
      if (applianceAreaToDrop.appliance_type !== activeCircuit.clipsal_monitors && !isEmptyApplianceArea) return;

      const hasValidDropArea = allAppliances.some((appliance) => {
        return appliance.appliance_type === activeCircuit.clipsal_monitors && appliance.appliance_id === dropAreaId;
      });

      // ignore this if the appliance is empty
      if (!hasValidDropArea && !isEmptyApplianceArea) return;

      dispatch(
        setAllCircuits(
          allCircuits.map((circuit) => {
            if (circuit.oem_circuit_id === draggedCircuitId) {
              return { ...circuit, appliance_id: Number(dropAreaId) };
            }
            return circuit;
          })
        )
      );

      // If the appliance area is empty, update the appliance name and type based on the circuit type.
      if (isEmptyApplianceArea) {
        const assignment = assignments.find((a) => a.assignment === activeCircuit.clipsal_monitors);
        dispatch(
          updateAppliance({
            applianceId: applianceAreaToDrop.appliance_id,
            name: assignment?.display_name ?? applianceAreaToDrop.appliance_name,
            type: activeCircuit.clipsal_monitors,
          })
        );
      }
    }
  }

  return (
    <DndContext
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      autoScroll={false}
      sensors={sensors}
      onDragEnd={handleDragEnd}
    >
      {/* Hacky fix for now as dndkit doesn't have support for react v19 as of yet */}
      {children as DndContextProps['children']}
    </DndContext>
  );
};
